import React, { useEffect, useState } from 'react';
import { useUpdateLanguageAndPreferencesMutation } from '@features/user';
import { Person as PersonIcon,Translate as LanguageIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { RoundedButton } from '../../styles/CustomButtons';
import { type LanguagesAndPreferences } from '../../types/types';

/**
 * Preview component showing how subject terminology appears in generated notes
 * Styled to match the BlockNote editor appearance
 */
const NotePreview = ({ subjectLanguage }: { subjectLanguage: string }) => {
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const subject = capitalize(subjectLanguage);

  // Highlighted text style matching the editor
  const highlightStyle = {
    bgcolor: 'primary.main',
    color: 'white',
    px: 0.5,
    py: 0.25,
    borderRadius: '4px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    display: 'inline',
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Sample label */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          textAlign: 'center',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Sample Note Preview
      </Typography>

      <Box
        sx={{
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'grey.200',
          textAlign: 'left',
        }}
      >
        {/* Section header - matches NoteSectionItem styling */}
        <Box
          sx={{
            pb: 1.5,
            mb: 2,
            borderBottom: '1px solid #E6E1FF',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            History of Present Illness
          </Typography>
        </Box>

        {/* Note content - matches BlockNote editor styling */}
        <Box
          sx={{
            fontSize: '1rem',
            lineHeight: 1.7,
            color: 'text.primary',
          }}
        >
          <Typography component="p" sx={{ mb: 1 }}>
            <Box component="span" sx={highlightStyle}>
              {subject}
            </Box>{' '}
            is a 45-year-old presenting with chief complaint of...
          </Typography>
          <Typography component="p">
            The{' '}
            <Box component="span" sx={highlightStyle}>
              {subjectLanguage}
            </Box>{' '}
            reports symptoms began approximately two weeks ago.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Language options for transcription (AssemblyAI)
export const languageNames = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  hi: 'Hindi',
  ja: 'Japanese',
  zh: 'Chinese',
  fi: 'Finnish',
  ko: 'Korean',
  pl: 'Polish',
  ru: 'Russian',
  tr: 'Turkish',
  uk: 'Ukrainian',
  vi: 'Vietnamese',
};

export interface LanguagePreferencesFormProps {
  /** Optional title to display at the top (e.g., for onboarding) */
  title?: string;
  /** Text for the submit button */
  submitButtonText?: string;
  /** Whether to use RoundedButton (onboarding) or regular Button (settings) */
  useRoundedButton?: boolean;
  /** Initial language preferences (for settings page) */
  languagesAndPreferences?: LanguagesAndPreferences;
  /** Callback when form is successfully submitted */
  onSuccessfulFormSubmission: (subjectLanguage?: string) => void;
}

/**
 * Shared component for language and subject preferences.
 * Used in both onboarding and settings pages.
 */
export const LanguagePreferencesForm = ({
  title,
  submitButtonText = 'Update Preferences',
  useRoundedButton = false,
  languagesAndPreferences,
  onSuccessfulFormSubmission,
}: LanguagePreferencesFormProps) => {
  const [updateLanguageAndPreferences] = useUpdateLanguageAndPreferencesMutation();

  const [languages, setLanguages] = useState<string[]>(['en']);
  const [subjectLanguage, setSubjectLanguage] = useState<string>('patient');
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize from props if provided (for settings page)
  useEffect(() => {
    if (languagesAndPreferences) {
      if (
        languagesAndPreferences.transcriptionLanguages &&
        languagesAndPreferences.transcriptionLanguages.length > 0
      ) {
        setLanguages(languagesAndPreferences.transcriptionLanguages);
      }
      if (languagesAndPreferences.userPreferences?.subject) {
        setSubjectLanguage(languagesAndPreferences.userPreferences.subject);
      }
    }
  }, [languagesAndPreferences]);

  const handleLanguageChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setLanguages(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    event.preventDefault();

    try {
      await updateLanguageAndPreferences({
        languages: languages,
        subject_language: subjectLanguage,
      }).unwrap();

      onSuccessfulFormSubmission(subjectLanguage);
      
      // Keep loading state active for onboarding flow (navigation will unmount component)
      // Only reset loading for settings page where user stays on same page
      if (!useRoundedButton) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to update language preferences:', error);
      setLoading(false);
    }
  };

  const renderButton = () => {
    if (useRoundedButton) {
      return (
        <Stack sx={{ justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <RoundedButton
            type="submit"
            variant="contained"
            color="primary"
            sx={{ px: 4, py: 1.5 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="body1" fontWeight={700}>
                {submitButtonText}
              </Typography>
            )}
          </RoundedButton>
        </Stack>
      );
    }

    return (
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={'24px'} /> : submitButtonText}
      </Button>
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {title && (
        <Typography
          variant="h4"
          gutterBottom
          component="div"
          fontWeight={700}
          align="center"
          sx={{ mb: 4 }}
        >
          {title}
        </Typography>
      )}

      {/* Language Selection */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <LanguageIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Transcription Languages
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the languages you speak during encounters. This helps our transcription engine
          accurately capture what's said.
        </Typography>
        <FormControl sx={{ display: 'block', maxWidth: 400, mx: 'auto' }}>
          <Select
            labelId="language-select-label"
            id="language-select"
            multiple
            fullWidth
            value={languages}
            onChange={handleLanguageChange}
            size="small"
            renderValue={selected =>
              selected.map(code => languageNames[code as keyof typeof languageNames]).join(', ')
            }
          >
            {Object.entries(languageNames).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                <Checkbox checked={languages.indexOf(code) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Subject Terminology */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <PersonIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Subject Terminology
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose how to refer to the subject of your encounters in generated notes.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="subject-language"
              name="subject-language"
              value={subjectLanguage}
              onChange={e => setSubjectLanguage(e.target.value)}
              row
            >
              <FormControlLabel value="patient" control={<Radio />} label="Patient" />
              <FormControlLabel value="client" control={<Radio />} label="Client" />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Live Preview */}
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <NotePreview subjectLanguage={subjectLanguage} />
        </Box>
      </Box>

      {renderButton()}
    </Box>
  );
};

